import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import { FunctionComponent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import EyeIcon from '../../assets/icons/eye.svg?react';
import LeadingIcon from '../../assets/icons/leading.svg?react';
import { Button } from '../../components/buttons';
import { Input } from '../../components/inputs';
import { useUserInfo } from '../userInfo/hooks';
import RoleSelect, { IRoleSelectItem } from './components/RoleSelect';

type Collaborator = {
  email: string;
  role: string;
};

type CollaboratorsFormModel = { collaborators: Collaborator[] };

interface IInviteWorkspaceInternalCollaboratorsProps {
  collaborators?: Collaborator[];
  collaboratorsChange?: (collaborators: Collaborator[]) => void;
  nextStep?: () => void;
  prevStep?: () => void;
}

const InviteWorkspaceInternalCollaborators: FunctionComponent<IInviteWorkspaceInternalCollaboratorsProps> = ({
  collaborators,
  collaboratorsChange,
  nextStep,
  prevStep,
}) => {
  const { t } = useTranslation('workspaces');
  const { t: tCommon } = useTranslation('COMMON');
  const { t: tAll } = useTranslation('ALL');

  const userInfo = useUserInfo();

  const validationSchema = Yup.object({
    collaborators: Yup.array(
      Yup.object({
        email: Yup.string()
          .email(t('edit.validations.not_valid_email'))
          .test('ends-with', t('create.invite_internal.domain_check'), (value) => {
            return !value || !!userInfo.company.domains.find((domain) => value?.endsWith('@' + domain)) || false;
          }),
        role: Yup.string().test('required-and-empty', t('edit.validations.required'), (value, control) => {
          return !control.parent.email || !!value;
        }),
      })
    ),
  });

  const roles: IRoleSelectItem[] = [
    {
      id: 'ADMIN',
      label: tCommon('admin'),
      description: tAll('adminDescription'),
      image: <LeadingIcon className={'w-5 h-5'} />,
    },
    {
      id: 'READONLY',
      label: tCommon('readonly'),
      description: tAll('readonlyDescription'),
      image: <EyeIcon className={'w-5 h-5'} />,
    },
  ];

  const [errorMessage, setErrorMessage] = useState<string>('');
  const myForm = useRef<FormikProps<CollaboratorsFormModel>>(null);

  const doSubmit = () => {
    myForm.current?.submitForm();
  };

  const handleOnSubmit = (form: CollaboratorsFormModel) => {
    collaboratorsChange?.(form.collaborators.filter((collaborator) => !!collaborator.email.trim()));
    nextStep?.();
  };

  const handleCancel = () => {
    collaboratorsChange?.(myForm.current?.values.collaborators.filter((collaborator) => !!collaborator.email.trim()) || []);
    prevStep?.();
  };

  return (
    <div className={'bg-wave flex items-center justify-center h-full w-full bg-gray-100'}>
      <div className={'flex flex-col items-center bg-white-100 p-8 shadow-md rounded-lg'} style={{ width: '448px' }}>
        <h3 className={'font-semibold mb-8 text-xl'}>{t('create.invite_internal.title')}</h3>
        <p className={'text-medium mb-8'} dangerouslySetInnerHTML={{ __html: t('create.invite_internal.description') }}></p>
        {errorMessage && <div className={'border-red-300 bg-red-600 text-white-100 w-full p-2 mb-3 rounded'}>{errorMessage}</div>}
        <Formik<CollaboratorsFormModel>
          initialValues={{
            collaborators: new Array(5)
              .fill({
                email: '',
                role: '',
              })
              .map((item, i) => (collaborators && collaborators.length > i ? collaborators[i] : { ...item })),
          }}
          onSubmit={handleOnSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={validationSchema}
          innerRef={myForm}
        >
          {({ values, errors, setFieldValue }) => {
            return (
              <Form className={'w-full'}>
                <table className='mb-3 w-full'>
                  {values.collaborators.map((collaborator, index) => (
                    <tr key={index}>
                      <td className={'pb-2'} valign={'top'}>
                        <Input
                          name='name'
                          id='name'
                          type='text'
                          value={collaborator.email}
                          placeholder={t('create.invite_internal.email')}
                          onChange={(e) => {
                            setFieldValue(`collaborators.${index}.email`, e.target.value);
                          }}
                          error={
                            errors.collaborators && errors.collaborators[index] ? (errors.collaborators[index] as FormikErrors<Collaborator>).email : undefined
                          }
                        />
                      </td>
                      <td className={'pb-2 pl-3'} valign={'top'}>
                        <RoleSelect
                          placeholder={tAll('choose')}
                          className={'w-full'}
                          items={roles}
                          value={collaborator.role}
                          onChange={(value) => setFieldValue(`collaborators.${index}.role`, value)}
                        />
                        {errors.collaborators && errors.collaborators[index] && (
                          <span className={'mt-2 text-sm text-red-600'}>{(errors.collaborators[index] as FormikErrors<Collaborator>).role}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </table>

                <div className='flex justify-end flex-col'>
                  <Button variant='primary' type='submit' className='justify-center py-2 px-4 w-full mb-4' onClick={doSubmit}>
                    {t('create.invite_internal.continue')}
                  </Button>
                  <Button variant='light' type='button' className='justify-center py-2 px-4 w-full' onClick={handleCancel}>
                    {t('create.invite_internal.back')}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default InviteWorkspaceInternalCollaborators;
